import { createRouter, createWebHistory } from "vue-router";

// route modules
import { registerRouter } from "./register.route";
import { packageRouter } from "./package.route";
import { voucherRouter } from "./voucher.route";
import { allotmentRouter } from "./allotment.route";
import { accountManagementRouter } from "./accountManagement.route";
import { analyticsRouter } from "./analytics.router";
import { menuRouter } from "./menu.route";
import { menuV2Router } from "./menuv2.route";
import { bookingRouter } from "./booking.route";
import { billingRouter } from "./billing.route";
import { reportRouter } from "./report.route";
import { pricingRulesRouter } from "./pricingRules.route";
import { localeTranslator } from "@/utils/global.util";

const DefaultLayout = () => import("@/layouts/default/DefaultLayout.vue");

const routes = [
  {
    path: "/login",
    name: "login",
    component: () => import("../pages/login/LoginPage.vue"),
    meta: { text: "$vuetify.login.login" },
  },
  {
    path: "/auto-login",
    name: "auto-login",
    component: () => import("../pages/login/AutoLogin.vue"),
    meta: { text: "$vuetify.login.login" },
  },
  {
    path: "/print-menu",
    name: "print-menu",
    component: () => import("../pages/print/PrintMenu.vue"),
    meta: { text: "$vuetify.menu.menuOverview" },
  },
  {
    path: "/admin-review/package-submissions/:submissionId",
    name: "admin-package-submission",
    component: () => import("../pages/package/main-package/PackageSubmission.vue"),
    meta: { text: "$vuetify.layout.navigationSidebar.mainPackage" },
  },
  ...registerRouter,
  ...allotmentRouter,
  ...menuRouter,
  ...menuV2Router,
  {
    path: "/global/helps",
    component: () => import("@/layouts/registration/RegistrationLayout.vue"),
    meta: { text: "$vuetify.layout.navigationSidebar.help" },
    children: [
      {
        path: "",
        name: "global-helps",
        component: () => import("../pages/help/HelpPage.vue"),
        meta: {
          text: "$vuetify.layout.navigationSidebar.help",
          style: "custom-page-style",
        },
      },
    ],
  },
  {
    path: "/",
    component: DefaultLayout,
    meta: { text: "$vuetify.layout.navigationSidebar.marketing" },
    children: [
      {
        path: "",
        name: "Dashboard",
        component: () => import("../pages/dashboard/DashboardPage.vue"),
        meta: {
          text: "$vuetify.layout.navigationSidebar.dashboard",
          defaultVisited: true,
        },
      },
      ...bookingRouter,
      ...voucherRouter,
      ...packageRouter,
      ...billingRouter,
      ...analyticsRouter,
      ...accountManagementRouter,
      ...pricingRulesRouter,
      {
        path: "help",
        name: "help",
        component: () => import("../pages/help/HelpPage.vue"),
        meta: { text: "$vuetify.layout.navigationSidebar.help" },
      },
      {
        path: "review/:id?",
        name: "Review Management",
        component: () => import("../pages/review-management/ReviewManagement.vue"),
        meta: { text: "$vuetify.layout.navigationSidebar.reviewManagement" },
      },
      {
        path: "marketing",
        name: "marketing",
        component: () => import("../pages/marketing/MarketingPage.vue"),
        meta: { text: "$vuetify.marketing.marketing" },
      },
      {
        path: "marketing/:id",
        name: "marketing-details",
        component: () => import("../pages/marketing/details/MarketingDetailsPage.vue"),
        meta: {
          text: "$vuetify.layout.navigationSidebar.marketingDetails",
          parent: {
            name: "$vuetify.layout.navigationSidebar.marketing",
            disabled: false,
            path: "/marketing",
            exact: true,
          },
        },
      },
      ...reportRouter,
      {
        path: "export",
        name: "Export History",
        component: () => import("../pages/export-history/ExportHistoryPage.vue"),
        meta: { text: "$vuetify.layout.navigationSidebar.exportHistory" },
      },
      {
        path: "allotment-history",
        name: "allotment-history",
        component: () => import("../pages/allotment/AllotmentHistory.vue"),
        meta: {
          text: "$vuetify.layout.navigationSidebar.editHistory",
          parent: { name: "$vuetify.layout.navigationSidebar.allotment" },
        },
      },
      {
        path: "/:pathMatch(.*)*",
        name: "not-found",
        component: () => import("../pages/error/404.vue"),
        meta: { text: "$vuetify.layout.navigationSidebar.pageNotFound" },
      },
    ],
  },
  {
    path: "/not-found-404",
    name: "not-found-fallback",
    component: () => import("../pages/error/404.vue"),
    meta: { text: "$vuetify.layout.navigationSidebar.pageNotFound" },
  },
  {
    path: "/unauthorized-401",
    name: "unauthorized-401",
    component: () => import("../pages/error/401.vue"),
  },
  {
    path: "/access-restricted-403",
    name: "access-restricted-403",
    component: () => import("../pages/error/403.vue"),
  },
  {
    path: "/set-password",
    name: "set-password",
    component: () => import("../pages/set-password/SetPassword.vue"),
    meta: { text: "$vuetify.setPassword.title" },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.afterEach((to) => {
  if (window.gtag) {
    const title = to.meta?.text ? localeTranslator(to.meta.text) : to.name || document.title;
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: to.fullPath,
      page_title: title,
    });
  }
});

export default router;
